import type { Element, LineElement, Page } from '@/components/canvas/document/publicTypes';
import { getElementAABB } from '@/components/canvas/workspace/controllers/marqueeSelectUtils';

export interface BoundsRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function unionBoundsFromRects(rects: BoundsRect[]): BoundsRect | null {
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }
  const width = maxX - minX;
  const height = maxY - minY;
  if (width <= 0 || height <= 0) return null;
  return { x: minX, y: minY, width, height };
}

/** 页内坐标下的元素联合包围盒（不含 line） */
export function unionBoundsFromPageElements(
  elements: Element[],
  dragOffset = { dx: 0, dy: 0 },
): BoundsRect | null {
  const rects = elements
    .filter((el) => el.type !== 'line')
    .map((el) => {
      const { x, y, w, h } = getElementAABB(el);
      return {
        x: x + dragOffset.dx,
        y: y + dragOffset.dy,
        width: w,
        height: h,
      };
    });
  return unionBoundsFromRects(rects);
}

/** 世界坐标：页内元素（layout 已为世界坐标） */
export function unionBoundsFromWorldElements(
  elements: Element[],
  dragOffset = { dx: 0, dy: 0 },
): BoundsRect | null {
  return unionBoundsFromPageElements(elements, dragOffset);
}

/** 选中 Page 板面（仅画板区域，不含标题栏）的世界坐标包围盒 */
export function pageSurfaceWorldBounds(page: Pick<Page, 'workspaceLayout' | 'size'>): BoundsRect {
  const { x, y } = page.workspaceLayout;
  return { x, y, width: page.size.width, height: page.size.height };
}

export function unionBoundsIncludingPages(
  elementRects: BoundsRect[],
  pages: Pick<Page, 'id' | 'workspaceLayout' | 'size'>[],
  pageIds: string[],
): BoundsRect | null {
  const pageRects = pages
    .filter((p) => pageIds.includes(p.id))
    .map((p) => pageSurfaceWorldBounds(p));
  return unionBoundsFromRects([...elementRects, ...pageRects]);
}

/** 将页内元素转为世界坐标（用于工作区圈选展示） */
export function elementToWorldElement(
  el: Element,
  pageX: number,
  pageY: number,
): Element {
  if (el.type === 'line') {
    const line = el as LineElement;
    return {
      ...el,
      layout: {
        ...el.layout,
        x: pageX + el.layout.x,
        y: pageY + el.layout.y,
      },
    };
  }
  return {
    ...el,
    layout: {
      ...el.layout,
      x: pageX + el.layout.x,
      y: pageY + el.layout.y,
    },
  };
}
